import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import { LIST_ERP_VERSAO_QUERYS_INIT, LIST_ALL_QUERYS_INIT, CREATE_QUERY_INIT, UPDATE_QUERY_INIT, DELETE_QUERY_INIT } from '../actionTypes';
import { FarmaError } from 'src/integracoes/modelos/erros';
import { listQuerysSuccess, listAllQuerysSuccess, createQuerySuccess, updateQuerySuccess, deleteQuerySuccess, requestErrorQuerys } from './actions';
import { listarQuerys, listarTodasQuerys, criarQuery, atualizarQuery, removerQuery } from 'src/integracoes/servicos/mongo-atlas/funcoes/querys';
import { IRespostaQuerys } from 'src/integracoes/servicos/mongo-atlas/modelos/IQuery';

// --------------------------- List Querys por ERP/Versão (parametrosIntegracao) ---------------------------------------------------------
export const fetchListarQuerys = async (erpId: string, versaoId: string) => {
  try {
    const { status, dados, mensagem } = await listarQuerys(erpId, versaoId);
    if (status && dados) {
      const { consultasPgFarma, consultasIqvia, consultasCloseup } = dados as IRespostaQuerys;
      const querys = [
        ...(consultasPgFarma ?? []).map((q: any) => ({ ...q, source: 'pgfarma' })),
        ...(consultasIqvia ?? []).map((q: any) => ({ ...q, source: 'iqvia' })),
        ...(consultasCloseup ?? []).map((q: any) => ({ ...q, source: 'closeup' }))
      ];
      return { isError: false, querys, errorMessage: null };
    }
    return { isError: true, querys: [], errorMessage: mensagem };
  } catch (error: any) {
    throw new FarmaError(error);
  }
};

function* parseListarQuerys({ payload }: any) {
  try {
    const { erpId, versaoId } = payload;
    const result: Awaited<ReturnType<typeof fetchListarQuerys>> = yield call(fetchListarQuerys, erpId, versaoId);
    if (!result.isError) {
      yield put(listQuerysSuccess(result.querys));
    } else {
      yield put(requestErrorQuerys(result.errorMessage || 'Erro inesperado'));
    }
  } catch (error: any) {
    yield put(requestErrorQuerys(error.mensagem ? `${error.mensagem}` : `${error.message}`));
  }
}

function* watchListQuerysInit() {
  yield takeEvery(LIST_ERP_VERSAO_QUERYS_INIT as any, parseListarQuerys);
}

// --------------------------- List Todas Querys (admin) ---------------------------------------------------------
const fetchListarTodasQuerys = async (params?: { versao?: string; erpId?: string; versaoId?: string }) => {
  try {
    const { status, dados, mensagem } = await listarTodasQuerys(params);
    if (status && Array.isArray(dados)) {
      return { isError: false, allQuerys: dados, errorMessage: null };
    }
    return { isError: true, allQuerys: [], errorMessage: mensagem };
  } catch (error: any) {
    throw new FarmaError(error);
  }
};

function* parseListarTodasQuerys({ payload }: any) {
  try {
    const { versao, erpId, versaoId } = payload ?? {};
    const params = (versao || erpId || versaoId) ? { versao, erpId, versaoId } : undefined;
    const { isError, allQuerys, errorMessage } = yield call(fetchListarTodasQuerys, params);
    if (!isError) {
      yield put(listAllQuerysSuccess({ allQuerys }));
    } else {
      yield put(requestErrorQuerys(errorMessage || 'Erro ao carregar queries'));
    }
  } catch (error: any) {
    const mensagem = error.mensagem ? `${error.mensagem}` : `${error.message}`;
    yield put(requestErrorQuerys(mensagem));
  }
}

function* watchListAllQuerysInit() {
  yield takeEvery(LIST_ALL_QUERYS_INIT as any, parseListarTodasQuerys);
}

// --------------------------- Criar Query ---------------------------------------------------------
const fetchCriarQuery = async (payload: any) => {
  try {
    const { status, dados, mensagem } = await criarQuery(payload);
    if (status && dados) {
      return { isError: false, query: dados, errorMessage: null };
    }
    return { isError: true, query: null, errorMessage: mensagem };
  } catch (error: any) {
    throw new FarmaError(error);
  }
};

function* parseCriarQuery({ payload }: any) {
  try {
    const { isError, query, errorMessage } = yield call(fetchCriarQuery, payload);
    if (!isError) {
      yield put(createQuerySuccess({ query }));
    } else {
      yield put(requestErrorQuerys(errorMessage || 'Erro ao criar query'));
    }
  } catch (error: any) {
    const mensagem = error.mensagem ? `${error.mensagem}` : `${error.message}`;
    yield put(requestErrorQuerys(mensagem));
  }
}

function* watchCreateQueryInit() {
  yield takeEvery(CREATE_QUERY_INIT as any, parseCriarQuery);
}

// --------------------------- Atualizar Query ---------------------------------------------------------
const fetchAtualizarQuery = async (id: string, payload: any) => {
  try {
    const { status, dados, mensagem } = await atualizarQuery(id, payload);
    if (status && dados) {
      return { isError: false, query: dados, errorMessage: null };
    }
    return { isError: true, query: null, errorMessage: mensagem };
  } catch (error: any) {
    throw new FarmaError(error);
  }
};

function* parseAtualizarQuery({ payload }: any) {
  try {
    const { id, ...rest } = payload;
    const { isError, query, errorMessage } = yield call(fetchAtualizarQuery, id, rest);
    if (!isError) {
      yield put(updateQuerySuccess({ query }));
    } else {
      yield put(requestErrorQuerys(errorMessage || 'Erro ao atualizar query'));
    }
  } catch (error: any) {
    const mensagem = error.mensagem ? `${error.mensagem}` : `${error.message}`;
    yield put(requestErrorQuerys(mensagem));
  }
}

function* watchUpdateQueryInit() {
  yield takeEvery(UPDATE_QUERY_INIT as any, parseAtualizarQuery);
}

// --------------------------- Remover Query ---------------------------------------------------------
const fetchRemoverQuery = async (id: string) => {
  try {
    const { status, mensagem } = await removerQuery(id);
    if (status) {
      return { isError: false, errorMessage: null };
    }
    return { isError: true, errorMessage: mensagem };
  } catch (error: any) {
    throw new FarmaError(error);
  }
};

function* parseRemoverQuery({ payload }: any) {
  try {
    const { id } = payload;
    const { isError, errorMessage } = yield call(fetchRemoverQuery, id);
    if (!isError) {
      yield put(deleteQuerySuccess({ id }));
    } else {
      yield put(requestErrorQuerys(errorMessage || 'Erro ao remover query'));
    }
  } catch (error: any) {
    const mensagem = error.mensagem ? `${error.mensagem}` : `${error.message}`;
    yield put(requestErrorQuerys(mensagem));
  }
}

function* watchRemoverQueryInit() {
  yield takeEvery(DELETE_QUERY_INIT as any, parseRemoverQuery);
}

export default function* querysSaga() {
  yield all([
    fork(watchListQuerysInit),
    fork(watchListAllQuerysInit),
    fork(watchCreateQueryInit),
    fork(watchUpdateQueryInit),
    fork(watchRemoverQueryInit)
  ]);
}
